import {useCallback, useEffect, useState} from 'react';

import {useAnnotationModeStore} from './AnnotationModeStore';
import {clearRegionHighlights, setRegionHighlights} from './annotationPaintCoordinator';
import type {Annotation, AnnotationAnchor} from './annotationTypes';
import {
  findAnnotationAtPoint,
  getSelectionOffsets,
  hasUntypesetMath,
  isHighlightApiSupported,
  observeSubtreeChanges,
  resolveAnnotationRanges
} from './annotationUtils';

const EMPTY_ANNOTATIONS: Annotation[] = [];

/**
 * `containerRef` is a callback ref so the paint effect re-runs if the element
 * attaches late (e.g. the feature flag resolving after first render).
 */
export function useAnnotations(regionId: string): {
  captureAnnotation: () => AnnotationAnchor | null;
  getAnnotationAtPoint: (x: number, y: number) => AnnotationAnchor | null;
  containerRef: (element: HTMLElement | null) => void;
} {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const annotations = useAnnotationModeStore(
    (state) => state.annotationsByRegionId[regionId] ?? EMPTY_ANNOTATIONS
  );
  const addAnnotation = useAnnotationModeStore((state) => state.addAnnotation);
  const setRegionHasAnnotatableContent = useAnnotationModeStore(
    (state) => state.setRegionHasAnnotatableContent
  );

  const captureAnnotation = useCallback((): AnnotationAnchor | null => {
    if (!container) {
      return null;
    }
    const selection = window.getSelection();
    const offsets = getSelectionOffsets(container);
    if (!selection || !offsets) {
      return null;
    }
    // Capture the rect before the selection is cleared below.
    const rect = selection.getRangeAt(0).getBoundingClientRect();
    const id = addAnnotation(regionId, offsets);
    // Clear the native selection so it is replaced by the painted annotation.
    selection.removeAllRanges();
    return {rect, id};
  }, [addAnnotation, container, regionId]);

  const getAnnotationAtPoint = useCallback(
    (x: number, y: number): AnnotationAnchor | null =>
      container ? findAnnotationAtPoint(container, x, y, annotations) : null,
    [container, annotations]
  );

  useEffect(() => {
    if (!container || annotations.length === 0 || !isHighlightApiSupported()) {
      return undefined;
    }
    const repaint = () =>
      setRegionHighlights(regionId, resolveAnnotationRanges(container, annotations));
    // Paint now unless MathJax will still rewrite the prompt — in that case the
    // observer paints once it settles, avoiding a flash against the stale DOM.
    if (!hasUntypesetMath(container)) {
      repaint();
    }
    // Highlight Ranges are live, so a painted annotation would slide as MathJax
    // rewrites the prompt. Clear this region on mutation, repaint once settled.
    const stopObserving = observeSubtreeChanges(container, {
      onMutate: () => clearRegionHighlights(regionId),
      onSettle: repaint
    });
    return () => {
      stopObserving();
      clearRegionHighlights(regionId);
    };
  }, [regionId, container, annotations]);

  useEffect(() => {
    setRegionHasAnnotatableContent(regionId, Boolean(container?.textContent?.trim()));
    return () => setRegionHasAnnotatableContent(regionId, false);
  }, [regionId, container, setRegionHasAnnotatableContent]);

  return {captureAnnotation, getAnnotationAtPoint, containerRef: setContainer};
}
